import { z } from "zod";
import { LessonVisit, ReviewAnswer } from "./feed.js";

/** Événements append-only générés sur l'appareil ; l'id (UUID v7) garantit l'idempotence de la synchro. */

export const AttemptEvent = z.object({
  id: z.string().uuid(),
  studentId: z.string().max(64),
  deviceId: z.string().max(80),
  exerciseTemplateId: z.string().max(64),
  params: z.record(z.number()).refine((p) => Object.keys(p).length <= 20, "trop de paramètres"),
  answer: z.unknown().nullable(),
  isCorrect: z.boolean().nullable(),
  hintsUsed: z.number().int().min(0).max(10).default(0),
  durationMs: z.number().int().min(0).max(24 * 3600_000),
  occurredAt: z.string().datetime(),
});
export type AttemptEvent = z.infer<typeof AttemptEvent>;

export const SessionEventType = z.enum([
  "SESSION_STARTED", "SESSION_PAUSED", "SESSION_RESUMED", "SESSION_ENDED",
  "LESSON_OPENED", "ANIMATION_STEP", "FIGURE_MANIPULATED", "EXERCISE_ANSWERED", "HINT_USED",
  "TUTOR_QUESTION", "APP_LEFT", "APP_RETURNED", "IDLE", "PIN_REMOVED", "WENT_OFFLINE", "BACK_ONLINE",
]);

export const SessionEvent = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  type: SessionEventType,
  occurredAt: z.string().datetime(),
  notionId: z.string().max(64).nullable().default(null),
  attemptId: z.string().uuid().nullable().default(null),
  // Petites métadonnées seulement (jamais le contenu d'un message) : 2 Ko au plus.
  data: z.record(z.unknown()).nullable().default(null).refine((d) => d == null || JSON.stringify(d).length <= 2048, "données trop volumineuses"),
});
export type SessionEvent = z.infer<typeof SessionEvent>;

/** Lots bornés : l'outbox de l'appareil envoie au plus 200 tentatives et 500 événements par appel. */
export const SyncBatch = z.object({
  deviceId: z.string().min(4).max(80),
  attempts: z.array(AttemptEvent).max(500).default([]),
  sessionEvents: z.array(SessionEvent).max(1000).default([]),
  lessonVisits: z.array(LessonVisit).max(50).default([]),
  reviews: z.array(ReviewAnswer).max(100).default([]),
});
export type SyncBatch = z.infer<typeof SyncBatch>;

export const SyncResult = z.object({
  accepted: z.object({ attempts: z.number().int(), sessionEvents: z.number().int(), lessonVisits: z.number().int().default(0), reviews: z.number().int().default(0) }),
  duplicates: z.object({ attempts: z.number().int(), sessionEvents: z.number().int(), lessonVisits: z.number().int().default(0), reviews: z.number().int().default(0) }),
  serverTime: z.string().datetime(),
});
export type SyncResult = z.infer<typeof SyncResult>;
